import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { debounceTime, filter, merge } from 'rxjs';

import { DataListCommandFilterType, FiltersToApply } from '@shared/components/data-list/common';
import { DataListFiltersComponent } from '@shared/components/data-list/data-list-command/filters/data-list-filters.component';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoFormModule } from '@shared/modules/forms.module';
import { TenantStore } from '@signal-store/tenant.store';
import { UserStore } from '@signal-store/user.store';

@Component({
  selector: 'app-data-list-command',
  templateUrl: './data-list-command.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatSidenavModule, CipoFormModule, DataListFiltersComponent],
})
export class DataListCommandComponent {
  tenantStore = inject(TenantStore);
  userStore = inject(UserStore);
  destroyRef = inject(DestroyRef);
  searchControl = new FormControl('');
  filterIsClear: boolean;
  private restoreFocus = false;
  private lastAppliedCriteria: string = null;

  sidenav = viewChild(MatSidenav);
  criteriaInput = viewChild<ElementRef<HTMLInputElement>>('criteriaInput');

  filters = input<DataListCommandFilterType[]>([]);
  hidefilter = input(false);
  hideReload = input(false);
  hideSearch = input(false);
  hasGlobalSearch = input(false);
  sidenavclass = input('');
  loading = input(false);
  filtersToApply = model<FiltersToApply>({});
  refreshed = output<void>();

  isGlobalSearch = signal(false);

  constructor() {
    effect(() => {
      const loading = this.loading();
      if (loading) {
        this.sidenav().close();
        this.searchControl.disable({ emitEvent: false });
      } else {
        this.searchControl.enable({ emitEvent: false });
      }
      if (!loading && this.restoreFocus) {
        this.criteriaInput()?.nativeElement.focus();
        this.restoreFocus = false;
      }
    });

    effect(() => {
      const applied = this.filtersToApply();
      untracked(() => {
        const criteria = applied.criteria ?? '';
        if ((this.searchControl.value ?? '') !== criteria) {
          this.searchControl.setValue(criteria, { emitEvent: false });
        }
        this.lastAppliedCriteria = criteria;
        const isGlobal = !!applied.isGlobalSearch;
        if (this.isGlobalSearch() !== isGlobal) {
          this.isGlobalSearch.set(isGlobal);
        }
      });
    });

    const delayedSearch$ = this.searchControl.valueChanges.pipe(
      debounceTime(DEBOUNCE_TIMES.long),
      filter(criteria => !!criteria),
    );
    const clearedSearch$ = this.searchControl.valueChanges.pipe(filter(criteria => !criteria));

    merge(delayedSearch$, clearedSearch$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(criteria => this.applyCriteria(criteria));
  }

  clearInput() {
    this.searchControl.reset();
  }

  search() {
    this.applyCriteria(this.searchControl.value);
  }

  resetFilter() {
    this.filterIsClear = true;
    this.filtersToApply.update(f => ({ ...f, filters: null, isQuickFilter: false }));
  }

  refreshTable() {
    this.filtersToApply.update(f => ({ ...f }));
    this.refreshed.emit();
  }

  toggleGlobalSearch() {
    const criteria = this.searchControl.value;
    if (!criteria) return;
    this.lastAppliedCriteria = criteria;
    this.isGlobalSearch.update(v => !v);
    this.filtersToApply.update(f => ({ ...f, criteria, isGlobalSearch: this.isGlobalSearch() }));
  }

  private applyCriteria(criteria: string) {
    if (criteria === this.lastAppliedCriteria) return;
    this.lastAppliedCriteria = criteria;
    this.restoreFocus = true;

    if (!criteria) {
      this.isGlobalSearch.set(false);
    }
    this.filtersToApply.update(f => ({ ...f, criteria, isGlobalSearch: this.isGlobalSearch() }));
  }
}
